import { Select } from "antd";
import { useGetCentralCountriesQuery } from "api/Country/hooks.ts";
import { useGetCountriesQuery } from "api/TenantSlug/Country/hooks.ts";
import { CountryFlag } from "components/UI/CountryFlag/CountryFlag.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { MULTISELECT_ALL_VALUE } from "localConstants";

import type { SelectProps } from "antd";

type OptionValue = string | number;

type CountrySelectSearchableProps = Omit<SelectProps<OptionValue | OptionValue[]>, "options"> & {
    withAllOption?: boolean;
    useNameAsValue?: boolean;
    useIso2AsValue?: boolean;
    source?: "central" | "tenant";
};

export const CountrySelectSearchable: React.FC<CountrySelectSearchableProps> = ({
    withAllOption = false,
    useNameAsValue = false,
    useIso2AsValue = false,
    source = "tenant",
    onChange,
    value,
    ...props
}) => {
    const tenantQuery = useGetCountriesQuery({ enabled: source === "tenant" });
    const centralQuery = useGetCentralCountriesQuery({ enabled: source === "central" });
    const { data, isLoading } = source === "central" ? centralQuery : tenantQuery;
    const { t } = usePaldockTranslation("forms.general-fields.country");
    const { t: tCommon } = usePaldockTranslation("common");

    const selected: OptionValue[] = Array.isArray(value) ? value : [];
    const isAllOptionSelected = selected.includes(MULTISELECT_ALL_VALUE);
    const isSomethingElseSelected = selected.some((val) => val !== MULTISELECT_ALL_VALUE);

    const countryOptions = (data?.countries ?? []).map((country) => ({
        label: (
            <span className="flex items-center gap-2">
                <CountryFlag country={country} />
                {country.name}
            </span>
        ),
        value: useIso2AsValue ? country.iso2 : useNameAsValue ? country.name : country.id,
        searchLabel: country.name,
        disabled: isAllOptionSelected,
    }));

    const options = withAllOption
        ? [
              {
                  label: tCommon("all"),
                  value: MULTISELECT_ALL_VALUE,
                  searchLabel: tCommon("all"),
                  disabled: isSomethingElseSelected,
              },
              ...countryOptions,
          ]
        : countryOptions;

    const handleChange = (
        selectedValues: OptionValue | OptionValue[],
        option: Parameters<NonNullable<SelectProps["onChange"]>>[1]
    ) => {
        if (!withAllOption || !Array.isArray(selectedValues)) {
            onChange?.(selectedValues, option);
            return;
        }

        let newValue: OptionValue[];

        if (selectedValues.includes(MULTISELECT_ALL_VALUE) && selectedValues.length > 1) {
            newValue = [MULTISELECT_ALL_VALUE];
        } else if (selectedValues.length === 0) {
            newValue = [];
        } else if (selectedValues.includes(MULTISELECT_ALL_VALUE)) {
            newValue = [MULTISELECT_ALL_VALUE];
        } else {
            newValue = selectedValues.filter((val) => val !== MULTISELECT_ALL_VALUE);
        }

        onChange?.(newValue, option);
    };

    return (
        <Select
            showSearch
            loading={isLoading}
            placeholder={t("placeholder")}
            options={options}
            optionFilterProp="label"
            filterOption={(input, option) =>
                (option?.searchLabel?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
            }
            {...props}
            onChange={handleChange}
            value={value}
            size={props.size ?? "middle"}
        />
    );
};
